import type { TinyEmitter } from "@expert/event-bus";
import type { WebSocketLike } from "react-use-websocket/dist/lib/types";

import { create } from "zustand";

import type { ExpertUniversityEvents } from "./src";

export interface ExpertUniversityStore {
    profileId?: string | undefined;
    sessionId?: string | undefined;
    eventBus?: TinyEmitter<ExpertUniversityEvents> | undefined;
    audioElements?: HTMLAudioElement[];
    isBotPlaying?: boolean;
    /**
     * Reconnect is set to false on the first connection, and then set to true
     * when the connection is closed to pass onto reconnected websocket connections
     * in the same session
     */
    reconnect?: boolean;
    /**
     * True from the moment a call ends until the evaluation flow resolves. The SDK sets this in
     * `hangupCall` *before* it marks the training task complete, which is what remounts
     * `ExpertUniversityLearningBlocks`. The component seeds its interim state from this flag on mount
     * so its very first render is the "call complete" interstitial — closing the window where the
     * default "Select a Profile" view would otherwise flash between task-completion and the
     * `trigger-evaluation-results` event arriving. The evaluation hook consumes/clears it on mount; it
     * is also cleared when a new session starts. It is deliberately *not* cleared in `resetEvents`,
     * which fires on websocket-close during hangup and would race the SDK setting it.
     */
    isEvaluationPending?: boolean;
    /**
     * The profile whose call set `isEvaluationPending`, so a consumer that only evaluates some profiles
     * can tell whether the pending evaluation is its own. Kept separate from `profileId` because
     * `resetEvents` clears that on websocket-close during hangup.
     */
    evaluationPendingProfileId?: string | undefined;
    setEvaluationPending: (isEvaluationPending: boolean, profileId?: string) => void;
    /**
     * True only when the server closed the LiveKit room. The evaluation interstitial reads it on
     * the task-completion remount. Cleared when the next session starts, and overwritten at the
     * start of every hangup. Not derived from the LiveKit disconnect reason string.
     */
    callClosedByServer?: boolean;
    setCallClosedByServer: (callClosedByServer: boolean) => void;
    setReconnect: (reconnect: boolean) => void;
    setIsBotPlaying: (isPlaying: boolean) => void;
    sendMessage?: (msg: ArrayBuffer | string, keep?: boolean) => void;
    getWebsocket?: () => WebSocketLike | null;
    setProfileId: (profileId: string | undefined) => void;
    setSessionId: (sessionId: string | undefined) => void;
    setWSFunctions: (
        send: (msg: ArrayBuffer | string, keep?: boolean) => void,
        getWs: () => WebSocketLike | null,
    ) => void;
    resetAudioElements: () => void;
    resetEvents: () => void;
    setEventBus: (eventBus: TinyEmitter | undefined) => void;
}

export const useExpertUniversityStore = create<ExpertUniversityStore>((set, _get) => ({
    setIsBotPlaying: (isPlaying) => set({ isBotPlaying: isPlaying }),
    setEvaluationPending: (isEvaluationPending, profileId) =>
        set({ isEvaluationPending, evaluationPendingProfileId: isEvaluationPending ? profileId : undefined }),
    setCallClosedByServer: (callClosedByServer) => set({ callClosedByServer }),
    setReconnect: (reconnect) => set({ reconnect }),
    setProfileId: (profileId) => set({ profileId }),
    setSessionId: (sessionId) => set({ sessionId }),
    setEventBus: (eventBus) => set({ eventBus }),
    audioElements: [],
    resetAudioElements: () => set({ audioElements: [] }),
    setWSFunctions: (send, getWs) => set({ sendMessage: send, getWebsocket: getWs }),
    resetEvents: () =>
        set({
            profileId: undefined,
            audioElements: [],
            isBotPlaying: false,
        }),
}));
